import React, { useState } from 'react'
import { Img } from 'remotion'
import {
  canRepair,
  getImageRepairHandler,
  initialRepairState,
  maxRetriesFor,
  onImageError,
  onRepaired,
  repairStateFor,
} from './imageRepairState'

export interface FallbackImgProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Img>,
  'src'
> {
  fallback?: React.ReactNode
  src?: string | null | undefined
}

// The handler lives in the pure module so the app can register it without
// importing this file, which would pull `remotion` into its entry chunk.
export {
  setImageRepairHandler,
  type ImageRepairHandler,
} from './imageRepairState'

/**
 * An image that draws nothing rather than a broken-image icon when it fails.
 *
 * `src` is loaded as is. It used to go through an imgix web proxy
 * (`zealous-avatars.imgix.net`) to resize it, which saved next to nothing — X
 * avatars are at most 400×400, ~24 KB at the median — while shipping the
 * signing token in the client bundle and making every render depend on a
 * legacy free-credit account (#429).
 *
 * With a repair handler registered (the browser), the first error asks it for
 * a replacement: see `imageRepairState.ts`.
 */
export const FallbackImg = ({ src, ...props }: FallbackImgProps) => {
  const [stored, setStored] = useState(() => initialRepairState(src))
  const state = repairStateFor(stored, src)
  const repairable = canRepair(state.url)

  const onError = () => {
    const next = onImageError(state, canRepair(state.url))
    setStored(next.state)
    const handler = getImageRepairHandler()
    if (!next.ask || !handler || !state.url) return

    const askedFor = src
    const failed = state.url
    handler
      .repair(failed)
      .catch(() => null)
      .then((replacement) =>
        setStored(
          (prev) => onRepaired(prev, { askedFor, failed, replacement }) ?? prev,
        ),
      )
  }

  return (
    <Img
      {...props}
      key={state.attempt}
      maxRetries={maxRetriesFor(repairable)}
      onError={onError}
      src={state.pending || !state.url ? transparent1x1 : state.url}
    />
  )
}

const transparent1x1 =
  'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=='
